export class NumberFormatter {
  /**
   * Formats a number with commas for display (e.g., 1000 -> "1,000")
   * @param value - The number to format
   * @returns Formatted string with commas
   */
  public static formatWithCommas(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    // Handle string values that might be in the middle of being typed (like "123.")
    if (typeof value === 'string') {
      // If the string ends with a decimal point, preserve it
      if (value.endsWith('.')) {
        const integerPart = value.slice(0, -1);
        const numValue = parseFloat(integerPart);
        if (!isNaN(numValue)) {
          return numValue.toLocaleString('en-US') + '.';
        }
      }
      
      // Convert to number if it's a string
      const numValue = parseFloat(value);
      
      // Check if it's a valid number
      if (isNaN(numValue)) {
        return '';
      }
      
      // Format with commas
      return numValue.toLocaleString('en-US');
    }
    
    // Format with commas
    return value.toLocaleString('en-US');
  }

  /**
   * Removes commas from a formatted number string to get the raw number
   * @param formattedValue - The formatted string with commas
   * @returns Raw number without commas
   */
  public static removeCommas(formattedValue: string | number | null | undefined): string {
    if (!formattedValue) {
      return '';
    }
    
    // Convert to string if it's a number
    const stringValue = typeof formattedValue === 'number' ? formattedValue.toString() : formattedValue;
    
    // Remove all commas
    return stringValue.replace(/,/g, '');
  }

  /**
   * Formats a number for input display with commas while preserving cursor position
   * @param value - The input value
   * @param cursorPosition - Current cursor position
   * @returns Object with formatted value and new cursor position
   */
  public static formatForInput(value: string, cursorPosition: number): { formattedValue: string; newCursorPosition: number } {
    if (!value) {
      return { formattedValue: '', newCursorPosition: 0 };
    }

    // Handle decimal input specially
    if (value.includes('.')) {
      const parts = value.split('.');
      const integerPart = this.removeCommas(parts[0]);
      const decimalPart = parts[1] || '';
      
      // Format the integer part with commas
      const formattedInteger = this.formatWithCommas(integerPart);
      
      // Combine formatted integer with decimal part
      const formattedValue = formattedInteger + (decimalPart ? '.' + decimalPart : '');
      
      // Calculate new cursor position
      const newCursorPosition = cursorPosition + (formattedValue.length - value.length);
      
      return { formattedValue, newCursorPosition };
    }

    // Remove existing commas to get raw number
    const rawValue = this.removeCommas(value);
    
    // Count commas before cursor position
    const commasBeforeCursor = (value.substring(0, cursorPosition).match(/,/g) || []).length;
    
    // Format the raw value
    const formattedValue = this.formatWithCommas(rawValue);
    
    // Calculate new cursor position
    const newCursorPosition = cursorPosition + (formattedValue.length - value.length);
    
    return { formattedValue, newCursorPosition };
  }

  /**
   * Validates if a string represents a valid number
   * @param value - The value to validate
   * @returns True if valid number, false otherwise
   */
  public static isValidNumber(value: string | number | null | undefined): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    
    const stringValue = typeof value === 'number' ? value.toString() : value;
    const rawValue = this.removeCommas(stringValue);
    
    return !isNaN(parseFloat(rawValue)) && isFinite(parseFloat(rawValue));
  }

  /**
   * Gets the raw numeric value from a formatted string
   * @param formattedValue - The formatted string or number
   * @returns Raw number or null if invalid
   */
  public static getRawNumber(formattedValue: string | number | null | undefined): number | null {
    if (!formattedValue) {
      return null;
    }
    
    // If it's already a number, return it
    if (typeof formattedValue === 'number') {
      return isNaN(formattedValue) ? null : formattedValue;
    }
    
    const rawValue = this.removeCommas(formattedValue);
    const numValue = parseFloat(rawValue);
    
    return isNaN(numValue) ? null : numValue;
  }
}
